import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Database } from "react-feather";
import { spacing } from "../../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../../design-system/tokens/typography";
import { rawColors } from "../../../../design-system/tokens/colors";
import { Spinner, Switch } from "../../../atoms";
import {
  DataTable,
  EmptyState,
  Pagination,
  SchemaTree,
  ScrollableTableViewport,
  type DataTableColumn,
  type SchemaTreeNode,
  type SchemaTreeTable,
} from "../../../molecules";
import {
  connectorAssetsApi,
  type ConnectorAssetSampleResponsePayload,
  type ConnectorAssetResponsePayload,
} from "../../../../services/api";
import type { StepRenderProps } from "../ConnectorWizardModal.types";
import {
  mapAssetsFromTreeResponse,
  mapFlatAssetsAsGroup,
  qualifiedKey,
  type TableMeta,
} from "./selectTreeMapper";

// ---------------------------------------------------------------------------
// Layout — 220px tree sidebar + flex preview pane.
// ---------------------------------------------------------------------------

const STEP_MIN_HEIGHT = "460px";
const FALLBACK_PREVIEW_PAGE_SIZE_OPTIONS = [5, 10] as const;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: ${STEP_MIN_HEIGHT};
`;

const Layout = styled.div`
  display: flex;
  gap: ${spacing[2]};
  width: 100%;
  flex: 1 1 auto;
  align-items: stretch;
  min-height: 0;
`;

const Right = styled.div`
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  overflow: hidden;
`;

const CenterFill = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewTitle = styled.h4`
  margin: 0;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
  padding: 0 ${spacing[1]};
  flex: 0 0 auto;
`;

const PreviewPaneFill = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PrimarySpinner = styled(Spinner).attrs({ size: 32 })`
  color: ${rawColors.primary[500]};
`;

const MutedIcon = styled(Database)`
  width: 28px;
  height: 28px;
  color: ${rawColors.text.lowEmphasis};
`;

const SystemToggleRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  ${typographyMixin("body", 4)}
  color: ${rawColors.text.mediumEmphasis};
  padding: 0 ${spacing[1]} ${spacing[2]};
  cursor: pointer;
  user-select: none;
`;

const InfoBanner = styled.div`
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background-color: ${rawColors.primary[50]};
  color: ${rawColors.text.mediumEmphasis};
  ${typographyMixin("body", 4)}
  padding: ${spacing[2]} ${spacing[3]};
  margin-bottom: ${spacing[2]};
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const safeTableList = (raw: unknown): string[] =>
  Array.isArray(raw)
    ? raw.filter((x): x is string => typeof x === "string")
    : [];

const titleCase = (s: string) =>
  s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);

interface PreviewRow {
  id: string;
  values: unknown[];
}

const normalizeErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) return error.message;
  return "Failed to load connector assets. Please retry.";
};

// Deduplicates concurrent fetches for the same slug+depth combination.
const inFlightRequests = new Map<
  string,
  Promise<ConnectorAssetResponsePayload[]>
>();

const fetchTreeAssets = async (
  catalogKey: string,
  maxDepth: number,
  pageLimit: number,
): Promise<ConnectorAssetResponsePayload[]> => {
  const key = `${catalogKey}::${maxDepth}::${pageLimit}`;
  const existing = inFlightRequests.get(key);
  if (existing) return existing;

  const request = connectorAssetsApi
    .listAll(catalogKey, {
      view: "tree",
      max_depth: maxDepth,
      limit: pageLimit,
    })
    .then((res) => res.items);

  inFlightRequests.set(key, request);
  try {
    return await request;
  } finally {
    inFlightRequests.delete(key);
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SelectStep: React.FC<StepRenderProps> = ({
  step,
  values,
  wizardConfig,
  catalogKey,
  setValue,
  setApiError,
}) => {
  const selectInfoMessage = useMemo(() => {
    const treeHints = wizardConfig.behavior?.treeHints;
    if (
      treeHints &&
      typeof treeHints === "object" &&
      typeof treeHints.select_info_message === "string" &&
      treeHints.select_info_message.trim().length > 0
    ) {
      return treeHints.select_info_message.trim();
    }
    if (typeof step.note === "string" && step.note.trim().length > 0) {
      return step.note.trim();
    }
    return null;
  }, [step.note, wizardConfig.behavior?.treeHints]);

  const previewPageSizeOptions = useMemo(() => {
    const fromConfig = wizardConfig.behavior?.previewPageSizeOptions;
    if (!Array.isArray(fromConfig) || fromConfig.length === 0) {
      return [...FALLBACK_PREVIEW_PAGE_SIZE_OPTIONS];
    }
    const sanitized = fromConfig.filter(
      (value): value is number =>
        typeof value === "number" && Number.isInteger(value) && value > 0,
    );
    return sanitized.length > 0
      ? sanitized
      : [...FALLBACK_PREVIEW_PAGE_SIZE_OPTIONS];
  }, [wizardConfig.behavior?.previewPageSizeOptions]);

  const defaultPreviewPageSize =
    previewPageSizeOptions[1] ?? previewPageSizeOptions[0] ?? 10;

  // HubSpot and Salesforce expose a single flat level of SaaS objects
  // (Contacts, Companies… / Account, Contact…) with no schema→table nesting.
  // Force max_depth=1 so we get a flat object list and group them under a
  // single virtual "Objects" node (via mapFlatAssetsAsGroup) so they're
  // selectable — otherwise the relational mapper renders each object as an
  // empty "schema" and nothing can be picked.
  const isFlatSaas = catalogKey === "hubspot" || catalogKey === "salesforce";
  const maxDepth = isFlatSaas
    ? 1
    : (wizardConfig.behavior?.assetSyncMaxDepth ?? 2);
  const pageLimit = wizardConfig.behavior?.assetSyncPageLimit ?? 500;

  const [assetsLoading, setAssetsLoading] = useState(true);
  const [assetsError, setAssetsError] = useState<string | null>(null);
  const [allAssets, setAllAssets] = useState<
    ConnectorAssetResponsePayload[] | null
  >(null);
  const [expandedSchemas, setExpandedSchemas] = useState<Set<string>>(
    new Set(),
  );
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSystemObjects, setShowSystemObjects] = useState(false);

  const [previewTarget, setPreviewTarget] = useState<SchemaTreeTable | null>(
    null,
  );
  const [previewPage, setPreviewPage] = useState(1);
  const [previewPageSize, setPreviewPageSize] = useState<number>(
    defaultPreviewPageSize,
  );
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewData, setPreviewData] =
    useState<ConnectorAssetSampleResponsePayload | null>(null);

  const selectedTables = useMemo(
    () => safeTableList(values.selectedTables),
    [values.selectedTables],
  );
  const selectedSet = useMemo(() => new Set(selectedTables), [selectedTables]);

  useEffect(() => {
    setPreviewPageSize(defaultPreviewPageSize);
  }, [defaultPreviewPageSize]);

  // ---- Fetch the tree once on entering the step ---------------------------
  useEffect(() => {
    let cancelled = false;
    setAssetsLoading(true);
    setAssetsError(null);
    setApiError(null);

    void (async () => {
      try {
        const items = await fetchTreeAssets(catalogKey, maxDepth, pageLimit);
        if (cancelled) return;
        setAllAssets(items);
      } catch (error: unknown) {
        if (cancelled) return;
        const message = normalizeErrorMessage(error);
        setAssetsError(message);
        setApiError(message);
      } finally {
        if (!cancelled) setAssetsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [catalogKey, maxDepth, pageLimit, setApiError]);

  // ---- Parse tree response into SchemaTreeNode[] --------------------------
  // max_depth=1 (HubSpot): items are flat leaves → group under "Objects".
  // max_depth>=2: items are schemas carrying children as tables.
  const { schemaNodes, tableMetaByKey, preSelectedKeys } = useMemo(() => {
    if (!allAssets) {
      return {
        schemaNodes: [] as SchemaTreeNode[],
        tableMetaByKey: {} as Record<string, TableMeta>,
        preSelectedKeys: new Set<string>(),
      };
    }
    if (maxDepth === 1) {
      // Salesforce's flat asset list mixes CRM objects (saas_object) with
      // natively-synced reports/dashboards (bi_view/bi_dashboard, see
      // salesforce_ingestion_service._build_native_dump_sync) — split into
      // two named groups within the same tree rather than lumping charts in
      // under "Objects", since only objects are previewable/selectable for
      // sync (charts are always synced, see isRowPreviewable below).
      if (catalogKey === "salesforce") {
        const objectItems = allAssets.filter(
          (a) => a.asset_type === "saas_object",
        );
        const chartItems = allAssets.filter(
          (a) => a.asset_type === "bi_view" || a.asset_type === "bi_dashboard",
        );
        const objectsResult = mapFlatAssetsAsGroup(objectItems, "Objects");
        const chartsResult = mapFlatAssetsAsGroup(
          chartItems,
          "Reports & Dashboards",
        );
        return {
          schemaNodes: [...objectsResult.schemaNodes, ...chartsResult.schemaNodes],
          tableMetaByKey: {
            ...objectsResult.tableMetaByKey,
            ...chartsResult.tableMetaByKey,
          },
          preSelectedKeys: new Set([
            ...objectsResult.preSelectedKeys,
            ...chartsResult.preSelectedKeys,
          ]),
        };
      }
      return mapFlatAssetsAsGroup(allAssets, "Objects");
    }
    return mapAssetsFromTreeResponse(allAssets);
  }, [allAssets, maxDepth, catalogKey]);

  // Default-expand every flat-SaaS group for better first-open UX (HubSpot
  // has just "Objects"; Salesforce also has "Reports & Dashboards" once its
  // assets are split above — expand whichever group names actually exist,
  // not a hardcoded single name).
  useEffect(() => {
    if (!isFlatSaas) return;
    const groupNames = schemaNodes.map((s) => s.name);
    if (groupNames.length === 0) return;
    setExpandedSchemas((prev) => {
      const missing = groupNames.filter((n) => !prev.has(n));
      if (missing.length === 0) return prev;
      const next = new Set(prev);
      missing.forEach((n) => next.add(n));
      return next;
    });
  }, [isFlatSaas, schemaNodes]);

  // Sync tableMetaByKey into wizard state (used by PreviewStep).
  useEffect(() => {
    setValue("tablesByKey", tableMetaByKey);
  }, [setValue, tableMetaByKey]);

  // Pre-populate selection from the backend's is_selected flags on first load.
  // Only applies when the user hasn't made any explicit selections yet.
  useEffect(() => {
    if (!allAssets || preSelectedKeys.size === 0) return;
    const currentSelected = safeTableList(values.selectedTables);
    if (currentSelected.length === 0) {
      setValue("selectedTables", Array.from(preSelectedKeys));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAssets]);

  // ---- Tree interaction handlers ------------------------------------------
  const onToggleSchemaExpand = (name: string) => {
    setExpandedSchemas((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const onToggleTableSelect = (
    schema: string,
    table: string,
    next: boolean,
  ) => {
    const key = qualifiedKey(schema, table);
    const nextList = next
      ? Array.from(new Set([...selectedTables, key]))
      : selectedTables.filter((k) => k !== key);
    setValue("selectedTables", nextList);
  };

  const onPreviewTable = (schema: string, table: string) => {
    setPreviewTarget({ schema, table });
  };

  const onSearchToggle = (open: boolean) => {
    setSearchOpen(open);
    if (!open) setSearchQuery("");
  };

  // ---- System-object filtering ---------------------------------------------
  // Salesforce/HubSpot-style Feed/History/Share/system objects are hidden by
  // default — they're queryable but return 0 rows unless specific org
  // activity happened, cluttering the tree with empty entries.
  const hasSystemObjects = useMemo(
    () => Object.values(tableMetaByKey).some((meta) => meta.isSystem),
    [tableMetaByKey],
  );

  const systemFilteredSchemaNodes: SchemaTreeNode[] = useMemo(() => {
    if (showSystemObjects) return schemaNodes;
    return schemaNodes
      .map((s): SchemaTreeNode | null => {
        const filteredTables = s.tables?.filter(
          (t) => !tableMetaByKey[qualifiedKey(s.name, t.table)]?.isSystem,
        );
        if (filteredTables === undefined || filteredTables.length > 0) {
          return { name: s.name, tables: filteredTables };
        }
        return null;
      })
      .filter((n): n is SchemaTreeNode => n !== null);
  }, [schemaNodes, showSystemObjects, tableMetaByKey]);

  // ---- Empty-object filtering (Salesforce only) ---------------------------
  // Row counts are front-loaded for every non-system object during initial
  // discovery (see SalesforceIngestionService._build_native_dump_sync), so
  // by the time this tree renders, a known-empty object's rowCount is a real
  // fetched 0, not just "we haven't asked yet." Hide those — no toggle, per
  // explicit request: "just show objects with data." Only filters when
  // rowCountKnown is true, so objects we simply haven't counted yet (e.g. on
  // a connection that predates row-count support) stay visible rather than
  // being wrongly treated as empty. Scoped to Salesforce only — HubSpot's
  // existing behavior (show everything; its counts come from a different,
  // always-complete discovery path) is left untouched.
  const isSalesforce = catalogKey === "salesforce";
  const dataFilteredSchemaNodes: SchemaTreeNode[] = useMemo(() => {
    if (!isSalesforce) return systemFilteredSchemaNodes;
    return systemFilteredSchemaNodes
      .map((s): SchemaTreeNode | null => {
        const filteredTables = s.tables?.filter((t) => {
          const meta = tableMetaByKey[qualifiedKey(s.name, t.table)];
          return !meta?.rowCountKnown || meta.rowCount > 0;
        });
        if (filteredTables === undefined || filteredTables.length > 0) {
          return { name: s.name, tables: filteredTables };
        }
        return null;
      })
      .filter((n): n is SchemaTreeNode => n !== null);
  }, [systemFilteredSchemaNodes, isSalesforce, tableMetaByKey]);

  // ---- Search filtering ---------------------------------------------------
  const trimmedQuery = searchQuery.trim().toLowerCase();
  const isSearching = trimmedQuery.length > 0;

  const treeNodes: SchemaTreeNode[] = useMemo(() => {
    return dataFilteredSchemaNodes
      .map((s): SchemaTreeNode | null => {
        if (!isSearching) return s;

        const schemaMatches = s.name.toLowerCase().includes(trimmedQuery);
        const filteredTables = s.tables?.filter((t) =>
          t.table.toLowerCase().includes(trimmedQuery),
        );
        const hasTableMatch =
          filteredTables !== undefined && filteredTables.length > 0;

        if (schemaMatches) return s;
        if (hasTableMatch) return { name: s.name, tables: filteredTables };
        return null;
      })
      .filter((n): n is SchemaTreeNode => n !== null);
  }, [dataFilteredSchemaNodes, isSearching, trimmedQuery]);

  // While searching, auto-expand schemas that contain a table name match.
  const effectiveExpanded = useMemo(() => {
    if (!isSearching) return expandedSchemas;
    const next = new Set(expandedSchemas);
    for (const node of treeNodes) {
      const hasTableMatch =
        Array.isArray(node.tables) &&
        node.tables.some((t) => t.table.toLowerCase().includes(trimmedQuery));
      if (hasTableMatch) next.add(node.name);
    }
    return next;
  }, [expandedSchemas, isSearching, treeNodes, trimmedQuery]);

  // ---- Preview pane -------------------------------------------------------
  const previewKey =
    previewTarget === null
      ? null
      : qualifiedKey(previewTarget.schema, previewTarget.table);
  const previewMeta = previewKey ? tableMetaByKey[previewKey] : undefined;

  const loadPreviewPage = async (
    assetId: string,
    page: number,
    pageSize: number,
  ): Promise<void> => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const response = await connectorAssetsApi.sample(catalogKey, assetId, {
        page,
        limit_rows: pageSize,
      });
      setPreviewData(response);
      setPreviewPage(response.page);
    } catch (error: unknown) {
      setPreviewError(normalizeErrorMessage(error));
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!previewMeta) {
      setPreviewData(null);
      setPreviewError(null);
      setPreviewPage(1);
      return;
    }
    setPreviewData(null);
    setPreviewError(null);
    void loadPreviewPage(previewMeta.tableAssetId, 1, previewPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewMeta?.tableAssetId, previewPageSize]);

  const previewRows: PreviewRow[] = useMemo(
    () =>
      (previewData?.rows ?? []).map((values, index) => ({
        id: `${previewData?.page ?? 1}-${index}`,
        values,
      })),
    [previewData?.rows, previewData?.page],
  );

  const previewColumns: Array<DataTableColumn<PreviewRow>> = useMemo(() => {
    const cols = previewData?.columns ?? [];
    if (cols.length === 0) return [];
    const width = `${100 / cols.length}%`;
    return cols.map((name, index) => ({
      key: `${name}-${index}`,
      header: name.toUpperCase(),
      width,
      render: (row) => {
        const value = row.values[index];
        return value === null || value === undefined ? "—" : String(value);
      },
    }));
  }, [previewData?.columns]);

  const previewResolvedPageSize = previewData?.limit_rows ?? previewPageSize;
  const previewTotalCount =
    previewData?.next_page !== null
      ? previewPage * previewResolvedPageSize + 1
      : (previewPage - 1) * previewResolvedPageSize + previewRows.length;
  const previewPageCount =
    previewData?.next_page !== null
      ? previewPage + 1
      : Math.max(previewPage, 1);

  const showPreviewPagination =
    previewData !== null &&
    !previewError &&
    previewColumns.length > 0 &&
    previewRows.length > 0;

  const showInitialPreviewSpinner = previewLoading && previewData === null;

  const handlePreviewPageChange = (nextPage: number) => {
    if (!previewMeta) return;
    if (nextPage < 1 || nextPage === previewPage) return;
    if (nextPage > previewPage && previewData?.next_page !== nextPage) return;
    void loadPreviewPage(previewMeta.tableAssetId, nextPage, previewPageSize);
  };

  // ---- Render -------------------------------------------------------------
  if (assetsLoading && !allAssets) {
    return (
      <StepContainer>
        <CenterFill>
          <EmptyState
            icon={<PrimarySpinner aria-hidden="true" />}
            caption="Syncing source and loading assets"
          />
        </CenterFill>
      </StepContainer>
    );
  }

  if (assetsError) {
    return (
      <StepContainer>
        <CenterFill>
          <EmptyState
            icon={<MutedIcon aria-hidden="true" />}
            caption={assetsError}
          />
        </CenterFill>
      </StepContainer>
    );
  }

  return (
    <StepContainer>
      {selectInfoMessage ? <InfoBanner>{selectInfoMessage}</InfoBanner> : null}
      {hasSystemObjects ? (
        <SystemToggleRow>
          <Switch
            value={showSystemObjects}
            onChange={setShowSystemObjects}
            size="sm"
            ariaLabel="Show system objects"
          />
          Show system objects (Feed, History, Share — usually empty)
        </SystemToggleRow>
      ) : null}
      <Layout>
        <SchemaTree
          schemas={treeNodes}
          expandedSchemas={effectiveExpanded}
          selectedTables={selectedSet}
          previewTarget={previewTarget}
          loadingSchemas={new Set()}
          qualifiedKey={qualifiedKey}
          searchQuery={searchQuery}
          searchOpen={searchOpen}
          onSearchToggle={onSearchToggle}
          onSearchChange={setSearchQuery}
          onToggleSchemaExpand={onToggleSchemaExpand}
          onToggleTableSelect={onToggleTableSelect}
          onPreviewTable={onPreviewTable}
          isRowPreviewable={
            isSalesforce
              ? (schema, table) =>
                  tableMetaByKey[qualifiedKey(schema, table)]?.assetType ===
                  "saas_object"
              : undefined
          }
          headerLabel={
            isSalesforce ? "Assets" : isFlatSaas ? "Objects" : undefined
          }
          searchPlaceholder={isFlatSaas ? "Search objects" : undefined}
          emptyChildLabel={isFlatSaas ? "No objects" : undefined}
          loadingChildLabel={isFlatSaas ? "Loading objects…" : undefined}
        />

        <Right>
          {!previewTarget ? (
            <CenterFill>
              <EmptyState
                icon={<MutedIcon aria-hidden="true" />}
                caption={
                  isFlatSaas
                    ? "Select an object to preview sample rows"
                    : "Select a table from a schema to view columns"
                }
              />
            </CenterFill>
          ) : (
            <>
              <PreviewTitle>
                {titleCase(previewTarget.table)}{" "}
                {isFlatSaas ? "object" : "table"}
              </PreviewTitle>
              <ScrollableTableViewport
                hideScrollbar
                insetTableChrome
                footer={
                  showPreviewPagination ? (
                    <Pagination
                      page={previewPage}
                      pageCount={previewPageCount}
                      pageSize={previewResolvedPageSize}
                      totalCount={previewTotalCount}
                      onPageChange={handlePreviewPageChange}
                      pageSizeOptions={previewPageSizeOptions}
                      onPageSizeChange={(nextPageSize) => {
                        setPreviewPageSize(nextPageSize);
                      }}
                    />
                  ) : undefined
                }
              >
                {showInitialPreviewSpinner ? (
                  <PreviewPaneFill>
                    <EmptyState
                      icon={<PrimarySpinner aria-hidden="true" />}
                      caption={`Loading ${previewTarget.table} preview`}
                    />
                  </PreviewPaneFill>
                ) : previewError ? (
                  <PreviewPaneFill>
                    <EmptyState
                      icon={<MutedIcon aria-hidden />}
                      caption={previewError}
                    />
                  </PreviewPaneFill>
                ) : previewColumns.length === 0 || previewRows.length === 0 ? (
                  <PreviewPaneFill>
                    <EmptyState
                      icon={<MutedIcon aria-hidden />}
                      caption={
                        isFlatSaas
                          ? "No preview rows available for this object."
                          : "No preview rows available for this table."
                      }
                    />
                  </PreviewPaneFill>
                ) : (
                  <DataTable
                    chromeless
                    stickyHeader
                    columns={previewColumns}
                    rows={previewRows}
                    rowKey={(row) => row.id}
                  />
                )}
              </ScrollableTableViewport>
            </>
          )}
        </Right>
      </Layout>
    </StepContainer>
  );
};

export default SelectStep;
